import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Spinner, EmptyState, Pill, Button, Modal, Input, Select, fmtEur } from "@/components/common";
import { Building2, Users, IdCard, CreditCard, LayoutGrid, Layers, Receipt, Settings, Eye, Pause, Play, Trash2, Plus, Pencil, TrendingUp } from "lucide-react";

const PLAN_CLS = { starter: "bg-slate-100 text-slate-700", standard: "bg-sky-100 text-sky-700", professional: "bg-violet-100 text-violet-700", enterprise: "bg-amber-100 text-amber-700" };
const STATUS_CLS = { active: "bg-emerald-100 text-emerald-700", trial: "bg-amber-100 text-amber-700", suspended: "bg-red-100 text-red-700" };

const TABS = [
  { id: "overview", label: "Panoramica", icon: LayoutGrid },
  { id: "tenants", label: "Associazioni", icon: Building2 },
  { id: "plans", label: "Piani", icon: Layers },
  { id: "transactions", label: "Transazioni", icon: Receipt },
  { id: "paypal", label: "PayPal", icon: Settings },
];

export default function SuperAdmin() {
  const [tab, setTab] = useState("overview");
  return (
    <div>
      <PageHeader title="Pannello Piattaforma" subtitle="Gestione clienti, piani e pagamenti" />
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${tab === t.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "tenants" && <Tenants />}
      {tab === "plans" && <Plans />}
      {tab === "transactions" && <Transactions />}
      {tab === "paypal" && <PayPalConfig />}
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e)); setD({}); }); }, []);
  if (!d) return <Spinner />;
  const kpis = [
    ["Associazioni", d.n_tenants, Building2, "text-slate-900"],
    ["Ricavi totali", fmtEur(d.revenue), TrendingUp, "text-emerald-600"],
    ["Tesserati totali", d.n_tesserati, IdCard, "text-sky-600"],
    ["Transazioni", d.n_transactions, CreditCard, "text-violet-600"],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {kpis.map(([l, v, Ic, cls]) => (
          <Card key={l} className="p-5" data-testid={`admin-kpi-${l}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider font-semibold text-slate-400">{l}</span><Ic size={18} className={cls} /></div>
            <p className={`text-2xl font-bold font-display mt-2 ${cls}`}>{v}</p>
          </Card>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {[["active", "Attive", d.active, "text-emerald-600"], ["trial", "In prova", d.trial, "text-amber-600"], ["suspended", "Sospese", d.suspended, "text-red-600"]].map(([k, l, v, cls]) => (
          <Card key={k} className="p-5"><p className="text-sm text-slate-500">{l}</p><p className={`text-3xl font-bold font-display ${cls}`}>{v}</p></Card>
        ))}
      </div>
    </div>
  );
}

function Tenants() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editT, setEditT] = useState(null);
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/admin/tenants").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const openDetail = (id) => api.get(`/admin/tenants/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
  const setStatus = async (t, status) => { try { await api.put(`/admin/tenants/${t.id}`, { plan_status: status }); toast.success("Aggiornato"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { if (!window.confirm("Eliminare l'ASD e TUTTI i suoi dati? Azione irreversibile.")) return; await api.delete(`/admin/tenants/${id}`); toast.success("ASD eliminata"); load(); };
  const saveEdit = async () => {
    try { await api.put(`/admin/tenants/${editT.id}`, { plan: editT.plan, plan_status: editT.plan_status, max_tesserati_override: editT.max_tesserati_override ? Number(editT.max_tesserati_override) : null }); toast.success("Salvato"); setEditT(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!items) return <Spinner />;
  return (
    <div>
      <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">{["ASD/SSD", "Admin", "Piano", "Tesserati", "Ricavi", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}</tr></thead>
        <tbody>{items.map((t) => (
          <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`tenant-row-${t.id}`}>
            <td className="px-4 py-3"><div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-lg bg-slate-900 grid place-items-center text-white text-xs font-bold">{t.name?.[0]}</div><div><p className="font-semibold text-slate-800">{t.name}</p><p className="text-xs text-slate-400">{t.tipo}</p></div></div></td>
            <td className="px-4 py-3 text-slate-500 text-xs">{t.admin_email || "—"}</td>
            <td className="px-4 py-3"><Pill className={PLAN_CLS[t.plan] || "bg-slate-100 text-slate-600"}>{t.plan_info?.name || t.plan}</Pill></td>
            <td className="px-4 py-3 text-slate-600">{t.n_tesserati} / {t.max_tesserati_override || t.plan_info?.max_tesserati || "∞"}</td>
            <td className="px-4 py-3 font-semibold text-emerald-600">{fmtEur(t.revenue)}</td>
            <td className="px-4 py-3"><Pill className={STATUS_CLS[t.plan_status] || "bg-slate-100"}>{t.plan_status}</Pill></td>
            <td className="px-4 py-3 text-right"><div className="flex justify-end gap-1">
              <button onClick={() => openDetail(t.id)} data-testid={`tenant-detail-${t.id}`} title="Dettaglio" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Eye size={15} /></button>
              <button onClick={() => setEditT({ ...t, max_tesserati_override: t.max_tesserati_override || "" })} data-testid={`tenant-edit-${t.id}`} title="Modifica piano" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>
              {t.plan_status === "suspended"
                ? <button onClick={() => setStatus(t, "active")} data-testid={`tenant-activate-${t.id}`} title="Riattiva" className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600"><Play size={15} /></button>
                : <button onClick={() => setStatus(t, "suspended")} data-testid={`tenant-suspend-${t.id}`} title="Sospendi" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Pause size={15} /></button>}
              <button onClick={() => del(t.id)} data-testid={`tenant-delete-${t.id}`} title="Elimina" className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
            </div></td>
          </tr>
        ))}</tbody>
      </table></div></Card>

      <Modal open={!!editT} onClose={() => setEditT(null)} title={`Gestisci ${editT?.name || ""}`}>
        {editT && <div className="space-y-4">
          <Select label="Piano" data-testid="edit-tenant-plan" value={editT.plan} onChange={(e) => setEditT({ ...editT, plan: e.target.value })}>{plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
          <Select label="Stato" value={editT.plan_status} onChange={(e) => setEditT({ ...editT, plan_status: e.target.value })}><option value="trial">Prova</option><option value="active">Attivo</option><option value="suspended">Sospeso</option></Select>
          <Input label="Limite tesserati personalizzato (opz.)" type="number" value={editT.max_tesserati_override} onChange={(e) => setEditT({ ...editT, max_tesserati_override: e.target.value })} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setEditT(null)}>Annulla</Button><Button onClick={saveEdit} data-testid="save-tenant-btn">Salva</Button></div>
        </div>}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.tenant?.name} wide>
        {detail && <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(detail.counts).map(([k, v]) => <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center"><p className="text-lg font-bold font-display text-slate-900">{v}</p><p className="text-xs text-slate-400 capitalize">{k}</p></div>)}
          </div>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex justify-between"><span className="text-slate-600">Ricavi totali</span><span className="font-bold text-emerald-700">{fmtEur(detail.revenue)}</span></div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Utenti ({detail.users.length})</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">{detail.users.map((u) => <div key={u.id} className="flex justify-between text-sm py-1.5 border-b border-slate-100"><span className="text-slate-700">{u.name} <span className="text-slate-400">· {u.email}</span></span><Pill className="bg-sky-100 text-sky-700">{u.role_label}</Pill></div>)}</div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Transazioni ({detail.transactions.length})</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">{detail.transactions.length === 0 ? <p className="text-sm text-slate-400">Nessuna transazione</p> : detail.transactions.map((x) => <div key={x.session_id} className="flex justify-between text-sm py-1.5 border-b border-slate-100"><span className="text-slate-600">{x.kind} {x.plan ? `· ${x.plan}` : ""}</span><span className="font-medium">{fmtEur(x.amount)} <Pill className={x.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{x.payment_status}</Pill></span></div>)}</div>
          </div>
        </div>}
      </Modal>
    </div>
  );
}

function Plans() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/plans").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm({ name: "", price: "", max_tesserati: 100, features: "", order: 10, active: true, _new: true }); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, price: p.price ?? "", features: (p.features || []).join("\n"), _new: false }); setOpen(true); };
  const save = async () => {
    const payload = { name: form.name, price: form.price === "" ? null : Number(form.price), max_tesserati: Number(form.max_tesserati), order: Number(form.order), active: form.active, features: form.features.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form._new) await api.post("/admin/plans", payload); else await api.put(`/admin/plans/${form.id}`, payload);
      toast.success("Piano salvato"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare il piano?")) return; try { await api.delete(`/admin/plans/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  if (!items) return <Spinner />;
  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={openNew} data-testid="add-plan-btn"><Plus size={16} /> Nuovo piano</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 flex flex-col" data-testid={`plan-${p.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-bold font-display text-lg text-slate-900">{p.name}</h3>
              {!p.active && <Pill className="bg-slate-100 text-slate-400">off</Pill>}
            </div>
            <p className="text-2xl font-bold font-display text-slate-900 mt-1">{p.price != null ? `€${p.price}` : "Su misura"}{p.price != null && <span className="text-sm text-slate-400">/mese</span>}</p>
            <p className="text-xs text-slate-400 mb-3">max {p.max_tesserati} tesserati</p>
            <ul className="text-sm text-slate-500 space-y-1 flex-1">{(p.features || []).map((f, i) => <li key={i}>· {f}</li>)}</ul>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}><Pencil size={13} /> Modifica</Button>
              <button onClick={() => del(p.id)} data-testid={`delete-plan-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500 border border-slate-200"><Trash2 size={15} /></button>
            </div>
          </Card>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={form?._new ? "Nuovo piano" : "Modifica piano"}>
        {form && <div className="space-y-4">
          <Input label="Nome" data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Prezzo €/mese (vuoto = su misura)" type="number" data-testid="plan-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input label="Max tesserati" type="number" value={form.max_tesserati} onChange={(e) => setForm({ ...form, max_tesserati: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Ordine" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
            <Select label="Attivo" value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}><option value="1">Sì</option><option value="0">No</option></Select>
          </div>
          <div><label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Caratteristiche (una per riga)</label>
            <textarea rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" /></div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-plan-btn">Salva</Button></div>
        </div>}
      </Modal>
    </div>
  );
}

function Transactions() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/transactions").then((r) => setItems(r.data)).catch((e) => { toast.error(apiErr(e)); setItems([]); }); }, []);
  if (!items) return <Spinner />;
  if (!items.length) return <Card className="p-6"><EmptyState icon={Receipt} title="Nessuna transazione" /></Card>;
  return (
    <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
      <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">{["Data", "ASD", "Tipo", "Importo", "Provider", "Stato"].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}</tr></thead>
      <tbody>{items.map((x) => (
        <tr key={x.session_id} className="border-b border-slate-100 hover:bg-slate-50/50">
          <td className="px-4 py-3 text-slate-500">{(x.created_at || "").slice(0, 10)}</td>
          <td className="px-4 py-3 font-medium text-slate-700">{x.tenant_name}</td>
          <td className="px-4 py-3 text-slate-600">{x.kind}{x.plan ? ` · ${x.plan}` : ""}</td>
          <td className="px-4 py-3 font-semibold">{fmtEur(x.amount)}</td>
          <td className="px-4 py-3 text-slate-500 capitalize">{x.provider || "—"}</td>
          <td className="px-4 py-3"><Pill className={x.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{x.payment_status}</Pill></td>
        </tr>
      ))}</tbody>
    </table></div></Card>
  );
}

function PayPalConfig() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ paypal_client_id: "", paypal_secret: "", environment: "sandbox" });
  useEffect(() => { api.get("/admin/config").then((r) => { setCfg(r.data); setForm({ paypal_client_id: r.data.paypal_client_id || "", paypal_secret: "", environment: r.data.environment || "sandbox" }); }).catch((e) => toast.error(apiErr(e))); }, []);
  const save = async () => {
    if (!form.paypal_client_id || !form.paypal_secret) { toast.error("Inserisci Client ID e Secret"); return; }
    try { await api.put("/admin/config", form); toast.success("Credenziali PayPal salvate"); setCfg({ ...cfg, paypal_client_id: form.paypal_client_id, has_secret: true, environment: form.environment }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!cfg) return <Spinner />;
  return (
    <Card className="p-6 max-w-xl">
      <h3 className="font-bold font-display text-lg text-slate-900 mb-1">Credenziali PayPal</h3>
      <p className="text-sm text-slate-500 mb-4">Inserisci le tue chiavi API PayPal (da developer.paypal.com). {cfg.has_secret && <span className="text-emerald-600 font-medium">Secret attualmente configurato ✓</span>}</p>
      <div className="space-y-4">
        <Input label="Client ID" data-testid="paypal-client-id" value={form.paypal_client_id} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} placeholder="A21AA..." />
        <Input label="Secret" data-testid="paypal-secret" type="password" value={form.paypal_secret} onChange={(e) => setForm({ ...form, paypal_secret: e.target.value })} placeholder={cfg.has_secret ? "•••••••• (invariato se vuoto? no: reinserisci)" : "EL1t..."} />
        <Select label="Ambiente" data-testid="paypal-env" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}><option value="sandbox">Sandbox (test)</option><option value="live">Live (produzione)</option></Select>
        <Button onClick={save} data-testid="save-paypal-config-btn">Salva credenziali</Button>
      </div>
    </Card>
  );
}
